import { createApi } from "@reduxjs/toolkit/query/react";
import { getAxiosInstance } from "../../../api";

export const NOTIFICATION_PAGE_SIZE = 10;

const axiosBaseQuery =
  () =>
  async ({ endpoint, method = "get", body }) => {
    const api = await getAxiosInstance();
    try {
      const response = await api[method](endpoint, body);
      return { data: response.data };
    } catch (axiosError) {
      return {
        error: {
          status: axiosError.response?.status,
          data: axiosError.response?.data || axiosError.message,
        },
      };
    }
  };

export const notification = createApi({
  reducerPath: "notificationApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Notification", "NotificationCount"],
  endpoints: (builder) => ({
    getNotificationList: builder.query({
      query: ({ page = 1, per_page = NOTIFICATION_PAGE_SIZE } = {}) => ({
        method: "get",
        endpoint: `/notifications/list?page=${page}&per_page=${per_page}`,
      }),
      transformResponse: (response) => ({
        data: Array.isArray(response?.data?.data) ? response.data.data : [],
        total_count: response?.data?.total_count ?? 0,
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, incoming, { arg }) => {
        if (!incoming) return;
        const incomingItems = Array.isArray(incoming.data) ? incoming.data : [];
        if (arg?.page === 1) {
          currentCache.data = incomingItems;
        } else {
          const existing = Array.isArray(currentCache.data)
            ? currentCache.data
            : [];
          const seen = new Set(existing.map((n) => n._id));
          currentCache.data = [
            ...existing,
            ...incomingItems.filter((n) => !seen.has(n._id)),
          ];
        }
        currentCache.total_count = incoming.total_count ?? 0;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page,
      providesTags: ["Notification"],
    }),

    getUnreadCount: builder.query({
      query: () => ({
        method: "get",
        endpoint: `/notifications/unread-count`,
      }),
      transformResponse: (response) => ({
        count: response?.data?.count ?? 0,
      }),
      providesTags: ["NotificationCount"],
    }),

    markAsRead: builder.mutation({
      query: (id) => ({
        method: "post",
        body: { id },
        endpoint: `/notifications/mark-as-read`,
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          notification.util.updateQueryData(
            "getNotificationList",
            undefined,
            (draft) => {
              const item = draft?.data?.find((n) => n._id === id);
              if (item) item.is_read = true;
            },
          ),
        );
        try {
          await queryFulfilled;
          dispatch(notification.util.invalidateTags(["NotificationCount"]));
        } catch {
          patch.undo();
        }
      },
    }),

    markAllAsRead: builder.mutation({
      query: () => ({
        method: "post",
        endpoint: `/notifications/mark-all-as-read`,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          notification.util.updateQueryData(
            "getNotificationList",
            undefined,
            (draft) => {
              draft?.data?.forEach((n) => {
                n.is_read = true;
              });
            },
          ),
        );
        try {
          await queryFulfilled;
          dispatch(notification.util.invalidateTags(["NotificationCount"]));
        } catch {
          patch.undo();
        }
      },
    }),
  }),
});

export const {
  useGetNotificationListQuery,
  useGetUnreadCountQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
} = notification;
